import React from 'react';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import getShownDifference from 'services/numbers/getShownDifference';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getExpensePerUnit from 'services/products/calculations/getExpensePerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import getWasteEffect from 'services/products/calculations/getWasteEffect';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import translations from 'services/translations/translations';

import { useCurrency } from 'services/offers/currency';
import ISubproduct from '../../ISubproduct';
import { getMarginClass } from '../getMarginRatioClassName';

const SubproductsSum = ({
  quantity,
  subproducts,
}: {
  quantity: number;
  subproducts: ISubproduct[];
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);
  const currency = useCurrency();

  const sellingPriceForUnit = getSellingPricePerUnit([], subproducts);
  const sellingPriceTotal = getSellingPriceTotal([], subproducts, quantity);
  const expensePerUnit = getExpensePerUnit([], subproducts);
  const expenseTotal = getExpenseTotal([], subproducts, quantity);
  const contributionMarginRatio = getContributionMarginRatio([], subproducts);

  // The margins shown: the shown selling price minus the shown expense, so they add up
  const contributionMarginPerUnit = getShownDifference(
    sellingPriceForUnit,
    expensePerUnit
  );

  const contributionMarginTotal = getShownDifference(
    sellingPriceTotal,
    expenseTotal
  );

  // The part of the waste of the material rows, for the whole position
  const waste = getWasteEffect(subproducts, quantity);
  const money = (value: number) => `${formatGroupedNumber(value)} ${currency}`;

  return (
    <Row className="calculation-sum-row">
      {/* A help box in a frame of its own, not to be mixed with the sums of the row */}
      <Col xs="4">
        {waste.expense === 0 && waste.sellingPrice === 0 ? null : (
          <div className="waste-effect" role="note">
            <div className="waste-effect-title">
              {t(`${translations.calculationWindow}:wasteEffect.Title`)}
            </div>
            <dl>
              <dt>{t(`${translations.offers}:Selling price`)}</dt>
              <dd>{money(waste.sellingPrice)}</dd>
              <dt>{t(`${translations.offers}:Expense`)}</dt>
              <dd>{money(waste.expense)}</dd>
              <dt>{t(`${translations.offers}:Contribution margin`)}</dt>
              <dd>{money(waste.contributionMargin)}</dd>
              <dt>{`${t(`${translations.offers}:Contribution margin`)} %`}</dt>
              <dd>{`${formatGroupedNumber(
                waste.contributionMarginRatio
              )} %`}</dd>
            </dl>
          </div>
        )}
      </Col>
      {/* The selling price and the margins green, as on the rows */}
      <Col className="sum-figure">
        <NumberWithUnitInputField
          disabled
          label={t(`${translations.offers}:Selling price`)}
          unit={currency}
          value={formatGroupedNumber(sellingPriceForUnit)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={t(`${translations.offers}:Expense`)}
          unit={currency}
          value={formatGroupedNumber(expensePerUnit)}
        />
      </Col>
      <Col className="sum-figure">
        <NumberWithUnitInputField
          disabled
          label={`${t(
            `${translations.offers}:Contribution margin`
          )} ${currency}`}
          unit={currency}
          value={formatGroupedNumber(contributionMarginPerUnit)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Selling price`)} ${t(
            `${translations.common}:total-short`
          )}`}
          unit={currency}
          value={formatGroupedNumber(sellingPriceTotal)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Expense`)} ${t(
            `${translations.common}:total-short`
          )}`}
          unit={currency}
          value={formatGroupedNumber(expenseTotal)}
        />
      </Col>
      <Col>
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Contribution margin`)} ${t(
            `${translations.common}:total-short`
          )}`}
          unit={currency}
          value={formatGroupedNumber(contributionMarginTotal)}
        />
      </Col>
      {/* Below 20 % red, above 30 % green, as on the rows */}
      <Col
        className={`sum-figure ${
          sellingPriceTotal === 0 ? '' : getMarginClass(contributionMarginRatio)
        }`}
      >
        <NumberWithUnitInputField
          disabled
          label={`${t(`${translations.offers}:Contribution margin`)} %`}
          unit="%"
          value={formatGroupedNumber(contributionMarginRatio)}
        />
      </Col>
    </Row>
  );
};

export default SubproductsSum;
